function createUuidV4(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }
}

function encodeHasAssistanceParams(entityCode: string, isChatAllowed: boolean): string {
  const strippedCode = entityCode.replace(/^0+/, '') || '0';
  const chatFlag = isChatAllowed ? '1' : '0';

  const uuid = crypto.randomUUID();
  const blocks = uuid.split('-');
  const blockSizes = [8, 4, 4, 4, 12];
  // Reserved 0-based positions: block 1 [1,2] and block 5 [9,10]
  const reserved: number[][] = [[1, 2], [], [], [], [9, 10]];

  const overlapsReserved = (blockIdx: number, start: number, len: number): boolean => {
    for (let i = start; i < start + len; i++) {
      if (reserved[blockIdx].includes(i)) return true;
    }
    return false;
  };

  const hexLetters = ['a', 'b', 'c', 'd', 'e', 'f'];
  const randomHexLetter = () => hexLetters[Math.floor(Math.random() * hexLetters.length)];

  const getValidStarts = (blockIdx: number, len: number, excludeStart?: number, needsTerminator = false): number[] => {
    const totalLen = needsTerminator ? len + 1 : len;
    const maxStart = Math.min(7, blockSizes[blockIdx] - totalLen);
    const starts: number[] = [];
    for (let s = 0; s <= maxStart; s++) {
      if (overlapsReserved(blockIdx, s, totalLen)) continue;
      if (excludeStart !== undefined && s === excludeStart) continue;
      starts.push(s);
    }
    return starts;
  };

  // Choose block for entityCode (blocks 2..5, 0-indexed: 1..4)
  const validEntityBlocks = [1, 2, 3, 4].filter(
    (bi) => getValidStarts(bi, strippedCode.length, undefined, true).length > 0,
  );
  const entityBlockIdx = validEntityBlocks[Math.floor(Math.random() * validEntityBlocks.length)];

  const entityStarts = getValidStarts(entityBlockIdx, strippedCode.length, undefined, true);
  const entityStartIdx = entityStarts[Math.floor(Math.random() * entityStarts.length)];

  // Choose block for isChatAllowed (blocks 1..5, 0-indexed: 0..4), different from entityCode block
  const validChatBlocks = [0, 1, 2, 3, 4].filter((bi) => {
    if (bi === entityBlockIdx) return false;
    return getValidStarts(bi, 1, entityStartIdx, true).length > 0;
  });
  const chatBlockIdx = validChatBlocks[Math.floor(Math.random() * validChatBlocks.length)];

  const chatStarts = getValidStarts(chatBlockIdx, 1, entityStartIdx, true);
  const chatStartIdx = chatStarts[Math.floor(Math.random() * chatStarts.length)];

  // Build result
  const blockArrays = blocks.map((b) => b.split(''));

  // Place entityCode in its block
  for (let i = 0; i < strippedCode.length; i++) {
    blockArrays[entityBlockIdx][entityStartIdx + i] = strippedCode[i];
  }
  // Place a hex letter terminator after entityCode to prevent digit bleed
  const entityEndIdx = entityStartIdx + strippedCode.length;
  if (entityEndIdx < blockSizes[entityBlockIdx]) {
    blockArrays[entityBlockIdx][entityEndIdx] = randomHexLetter();
  }

  // Place isChatAllowed flag in its block
  blockArrays[chatBlockIdx][chatStartIdx] = chatFlag;
  // Place a hex letter terminator after isChatAllowed to prevent digit bleed
  const chatEndIdx = chatStartIdx + 1;
  if (chatEndIdx < blockSizes[chatBlockIdx]) {
    blockArrays[chatBlockIdx][chatEndIdx] = randomHexLetter();
  }

  // Metadata in block 1, positions 2..3 (0-indexed 1..2)
  blockArrays[0][1] = (entityBlockIdx + 1).toString(16);
  blockArrays[0][2] = (entityStartIdx + 1).toString(16);

  // Metadata in block 5, positions 10..11 (0-indexed 9..10)
  blockArrays[4][9] = (chatBlockIdx + 1).toString(16);
  blockArrays[4][10] = (chatStartIdx + 1).toString(16);

  return blockArrays.map((b) => b.join('')).join('-');
}

function decodeHasAssistanceParams(encoded: string): { entityCode: string; isChatAllowed: boolean } | null {
  const blocks = encoded.split('-');
  if (blocks.length !== 5) return null;

  const blockSizes = [8, 4, 4, 4, 12];
  if (blocks.some((b, i) => b.length !== blockSizes[i])) return null;

  // Read metadata from block 1, positions 2..3 (0-indexed 1..2)
  const entityBlockIdx = parseInt(blocks[0][1], 16) - 1;
  const entityStartIdx = parseInt(blocks[0][2], 16) - 1;

  // Read metadata from block 5, positions 10..11 (0-indexed 9..10)
  const chatBlockIdx = parseInt(blocks[4][9], 16) - 1;
  const chatStartIdx = parseInt(blocks[4][10], 16) - 1;

  if ([entityBlockIdx, entityStartIdx, chatBlockIdx, chatStartIdx].some((v) => isNaN(v) || v < 0)) return null;
  if (entityBlockIdx < 0 || entityBlockIdx > 4) return null;
  if (chatBlockIdx < 0 || chatBlockIdx > 4) return null;

  // Extract entityCode: read digits starting at entityStartIdx until a non-digit is found or block ends
  let entityCode = '';
  for (let i = entityStartIdx; i < blocks[entityBlockIdx].length; i++) {
    const ch = blocks[entityBlockIdx][i];
    if (ch >= '0' && ch <= '9') {
      entityCode += ch;
    } else {
      break;
    }
  }

  // Pad to 8 characters with leading zeros
  entityCode = entityCode.padStart(8, '0');

  // Extract isChatAllowed flag
  const chatFlag = blocks[chatBlockIdx][chatStartIdx];
  const isChatAllowed = chatFlag === '1';

  return { entityCode, isChatAllowed };
}

export { createUuidV4, encodeHasAssistanceParams, decodeHasAssistanceParams };
